"use client";

import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import DropdownSelect from "../../../components/admin/common/DropDownSelect";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import { Pagination } from "../../../components/admin/common/PaginationNumber";
import { MemberListTable } from "../../../components/admin/members/MemberListTable";
import { getMembersByStatus } from "../../../services/memberServices";
import { getAllDesignations } from "../../../services/designationServices";
import ExportButtons from "../../../components/admin/common/ExportButtons";
import { useUserModulePermissions } from "../../../hooks/useUserModulePermissions";
import { getCookie } from "../../../utils";

const entriesOptions = [
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 50, label: "50" },
];

const MembersList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Add ref for scrolling to member list
  const memberListRef = useRef(null);

  const [entriesOption, setEntriesOption] = useState(entriesOptions[0]);
  const [page, setPage] = useState(1);
  const [memberList, setMemberList] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalDocs, setTotalDocs] = useState(0);
  const [designations, setDesignations] = useState([]);
  const fixedStatus = "all";

  // Filter states
  const [searchInput, setSearchInput] = useState("");
  const [searchType, setSearchType] = useState("name");
  const [alphabetFilter, setAlphabetFilter] = useState("");
  const [activeRole, setActiveRole] = useState("");
  const [filters, setFilters] = useState({
    name: "",
    membershipId: "",
    fieldOfWork: "",
    speciesOfInterest: "",
    natureOfWork: "",
    designation: "",
    currentOfficeBearers: "",
    memberRole: "",
    alphabetFilter: "",
    pastPresident: "", // Add pastPresident to filters
  });

  const currentOfficeBearers = [
    { id: "6896dec7da48601d05b73ab0", title: "President", order: 1 },
    {
      id: "6896ded8da48601d05b73ab3",
      title: "Vice President (Membership)",
      order: 2,
    },
    {
      id: "6896dee7da48601d05b73ab6",
      title: "Vice President (Events)",
      order: 3,
    },
    {
      id: "6896def5da48601d05b73ab9",
      title: "Vice President (Education)",
      order: 4,
    },
    { id: "6896df04da48601d05b73abc", title: "General Secretary", order: 5 },
    { id: "6896df10da48601d05b73abf", title: "Joint Secretary", order: 6 },
    { id: "6896df21da48601d05b73ac2", title: "Treasurer", order: 7 },
    { id: "6896df2bda48601d05b73ac5", title: "Joint Treasurer", order: 8 },
  ];

  const currentOfficeBearerIds = currentOfficeBearers.map(
    (bearer) => bearer.id
  );

  const searchTypes = [
    { value: "name", label: "By Name", placeholder: "Enter member name..." },
    {
      value: "membershipId",
      label: "By Membership No",
      placeholder: "Enter membership number...",
    },
    {
      value: "fieldOfWork",
      label: "By Field of Work",
      placeholder: "Enter field of work...",
    },
    {
      value: "speciesOfInterest",
      label: "By Species of Interest",
      placeholder: "Enter species of interest...",
    },
    {
      value: "natureOfWork",
      label: "By Nature of Work",
      placeholder: "Enter nature of work...",
    },
  ];

  // Function to scroll to member list
  const scrollToMemberList = () => {
    if (memberListRef.current) {
      memberListRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Helper function to parse URL parameters
  const parseURLParams = () => {
    const searchParams = new URLSearchParams(location.search);
    const urlParams = {
      name: searchParams.get("name") || "",
      membershipId: searchParams.get("membershipId") || "",
      fieldOfWork: searchParams.get("fieldOfWork") || "",
      speciesOfInterest: searchParams.get("speciesOfInterest") || "",
      natureOfWork: searchParams.get("natureOfWork") || "",
      designation: searchParams.get("designation") || "",
      currentOfficeBearers: searchParams.get("currentOfficeBearers") || "",
      memberRole: searchParams.get("memberRole") || "",
      alphabetFilter: searchParams.get("alphabetFilter") || "",
      pastPresident: searchParams.get("pastPresident") || "", // Add pastPresident
      isCurrentOfficeBearer: searchParams.get("isCurrentOfficeBearer") || "",
      page: parseInt(searchParams.get("page") || "1", 10),
    };

    // Handle special cases for URL parameters
    if (urlParams.isCurrentOfficeBearer === "true") {
      urlParams.currentOfficeBearers = currentOfficeBearerIds.join(",");
      urlParams.memberRole = "";
    }

    return urlParams;
  };

  // Helper function to update URL with current filters
  const updateURL = (newFilters, newPage = page) => {
    const searchParams = new URLSearchParams();

    // Add non-empty filters to URL
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && key !== "isCurrentOfficeBearer") {
        searchParams.set(key, value);
      }
    });

    // Add page if not 1
    if (newPage > 1) {
      searchParams.set("page", newPage.toString());
    }

    const newURL = `${location.pathname}${
      searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`;
    navigate(newURL, { replace: true });
  };

  // Fetch designations
  useEffect(() => {
    const fetchDesignations = async () => {
      try {
        const data = await getAllDesignations();
        const allowedNames = ["EC Member", "Founder Member", "Member"]; // Removed "Past President" from here
        const filtered = data.data.data.filter((designation) =>
          allowedNames.includes(designation.name)
        );
        setDesignations(filtered);
      } catch (err) {
        console.error(err.message);
      }
    };
    fetchDesignations();
  }, []);

  // Initialize state from URL parameters on component mount
  useEffect(() => {
    const urlParams = parseURLParams();

    // Set page
    setPage(urlParams.page > 0 ? urlParams.page : 1);

    // Set filters
    const newFilters = {
      name: urlParams.name,
      membershipId: urlParams.membershipId,
      fieldOfWork: urlParams.fieldOfWork,
      speciesOfInterest: urlParams.speciesOfInterest,
      natureOfWork: urlParams.natureOfWork,
      designation: urlParams.designation,
      currentOfficeBearers: urlParams.currentOfficeBearers,
      memberRole: urlParams.memberRole,
      alphabetFilter: urlParams.alphabetFilter,
      pastPresident: urlParams.pastPresident, // Add pastPresident
    };

    setFilters(newFilters);

    // Set search input and type based on URL params
    if (urlParams.name) {
      setSearchInput(urlParams.name);
      setSearchType("name");
    } else if (urlParams.membershipId) {
      setSearchInput(urlParams.membershipId);
      setSearchType("membershipId");
    } else if (urlParams.fieldOfWork) {
      setSearchInput(urlParams.fieldOfWork);
      setSearchType("fieldOfWork");
    } else if (urlParams.speciesOfInterest) {
      setSearchInput(urlParams.speciesOfInterest);
      setSearchType("speciesOfInterest");
    } else if (urlParams.natureOfWork) {
      setSearchInput(urlParams.natureOfWork);
      setSearchType("natureOfWork");
    }

    // Set alphabet filter
    if (urlParams.alphabetFilter) {
      setAlphabetFilter(urlParams.alphabetFilter);
    }

    // Set active role based on filters
    if (urlParams.currentOfficeBearers) {
      setActiveRole("Current Office Bearers");
    } else if (urlParams.memberRole === "Founder Member") {
      setActiveRole("Founder Member");
    } else if (urlParams.memberRole === "Lifetime Members") {
      setActiveRole("Lifetime Members");
    } else if (urlParams.pastPresident === "true") {
      // Check for pastPresident boolean
      setActiveRole("Past President");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Set active role based on designation after designations are loaded
  useEffect(() => {
    if (designations.length > 0 && filters.designation) {
      const roleData = designations.find(
        (role) => role._id === filters.designation
      );
      if (roleData) {
        setActiveRole(roleData.name);
      }
    }
  }, [designations, filters.designation]);

  // Updated query to include filters
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["members", page, entriesOption.value, filters, fixedStatus],
    queryFn: () =>
      getMembersByStatus(fixedStatus, page, entriesOption.value, filters),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data?.data?.members) {
      setMemberList(data.data.members);
      setTotalPages(data.data.totalPages || 1);
      setHasMore(data.data.hasMore || false);
      setTotalDocs(data.data.totalDocs || 0);
    }
  }, [data]);

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
    updateURL(filters, pageNum);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEntriesChange = (option) => {
    setEntriesOption(option);
    setPage(1);
    updateURL(filters, 1);
  };

  // FIXED: Handle search type change - clear search input when changing type
  const handleSearchTypeChange = (newSearchType) => {
    setSearchType(newSearchType);
    setSearchInput(""); // Clear search input when changing type
  };

  // FIXED: Updated search handler
  const handleSearch = () => {
    // Create reset filters object
    const resetFilters = {
      name: "",
      membershipId: "",
      fieldOfWork: "",
      speciesOfInterest: "",
      natureOfWork: "",
      designation: "",
      currentOfficeBearers: "",
      memberRole: "",
      alphabetFilter: "",
      pastPresident: "", // Add pastPresident
    };

    // Apply search filter if there's input
    if (searchInput.trim()) {
      resetFilters[searchType] = searchInput.trim();
    }

    // Clear all role-related states when searching
    setActiveRole("");
    setAlphabetFilter("");

    setFilters(resetFilters);
    setPage(1);
    updateURL(resetFilters, 1);

    // Scroll to member list after a short delay
    setTimeout(() => {
      scrollToMemberList();
    }, 100);
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setSearchType("name");
    setAlphabetFilter("");
    setActiveRole("");

    const resetFilters = {
      name: "",
      membershipId: "",
      fieldOfWork: "",
      speciesOfInterest: "",
      natureOfWork: "",
      designation: "",
      currentOfficeBearers: "",
      memberRole: "",
      alphabetFilter: "",
      pastPresident: "", // Add pastPresident
    };

    setFilters(resetFilters);
    setPage(1);
    updateURL(resetFilters, 1);

    // Scroll to member list after a short delay
    setTimeout(() => {
      scrollToMemberList();
    }, 100);
  };

  // FIXED: Updated role filter handler
  const handleRoleFilter = (role) => {
    let newActive = activeRole === role ? "" : role;
    let updatedFilters = { ...filters };

    // Clear search-related states when applying role filter
    setSearchInput("");
    setSearchType("name");
    setAlphabetFilter("");

    // Clear all search filters when applying role filter
    updatedFilters = {
      name: "",
      membershipId: "",
      fieldOfWork: "",
      speciesOfInterest: "",
      natureOfWork: "",
      designation: "",
      currentOfficeBearers: "",
      memberRole: "",
      alphabetFilter: "",
      pastPresident: "", // Add pastPresident
    };

    if (role === "Current Office Bearers") {
      updatedFilters = {
        ...updatedFilters,
        currentOfficeBearers: newActive ? currentOfficeBearerIds.join(",") : "",
      };
    } else if (role === "Founder Member") {
      updatedFilters = {
        ...updatedFilters,
        memberRole: newActive ? "Founder Member" : "",
      };
    } else if (role === "Past President") {
      // Handle Past President with boolean
      updatedFilters = {
        ...updatedFilters,
        pastPresident: newActive ? "true" : "", // Set boolean string value
      };
    } else {
      // Handle designation roles
      const roleData = designations.find((r) => r.name === role);
      updatedFilters = {
        ...updatedFilters,
        designation: roleData && newActive ? roleData._id : "",
      };
    }

    // If deactivating role, clear everything
    if (!newActive) {
      newActive = "";
      updatedFilters = {
        name: "",
        membershipId: "",
        fieldOfWork: "",
        speciesOfInterest: "",
        natureOfWork: "",
        designation: "",
        currentOfficeBearers: "",
        memberRole: "",
        alphabetFilter: "",
        pastPresident: "", // Add pastPresident
      };
    }

    setActiveRole(newActive);
    setFilters(updatedFilters);
    setPage(1);
    updateURL(updatedFilters, 1);

    // Scroll to member list after a short delay
    setTimeout(() => {
      scrollToMemberList();
    }, 100);
  };

  const handleAlphabetFilter = (letter) => {
    const newAlphabetFilter = alphabetFilter === letter ? "" : letter;
    setAlphabetFilter(newAlphabetFilter);

    const updatedFilters = {
      ...filters,
      alphabetFilter: newAlphabetFilter,
    };

    setFilters(updatedFilters);
    setPage(1);
    updateURL(updatedFilters, 1);

    // Scroll to member list after a short delay
    setTimeout(() => {
      scrollToMemberList();
    }, 100);
  };

  const shouldShowAlphabetFilter = () => {
    return (
      filters.designation === "6896f0a379b374d7da63d536" &&
      activeRole &&
      activeRole !== "Current Office Bearers" &&
      activeRole !== "Founder Member" &&
      activeRole !== "Past President" // Add Past President to conditions
    );
  };

  const getCurrentPlaceholder = () => {
    const currentType = searchTypes.find((type) => type.value === searchType);
    return currentType ? currentType.placeholder : "Enter search term...";
  };

  const removeFilter = (filterKey) => {
    const updatedFilters = { ...filters };
    updatedFilters[filterKey] = "";

    if (filterKey === "alphabetFilter") {
      setAlphabetFilter("");
    }

    if (
      filterKey === "designation" ||
      filterKey === "currentOfficeBearers" ||
      filterKey === "memberRole" ||
      filterKey === "pastPresident" // Add pastPresident
    ) {
      setActiveRole("");
      updatedFilters.currentOfficeBearers = "";
      updatedFilters.designation = "";
      updatedFilters.memberRole = "";
      updatedFilters.pastPresident = ""; // Add pastPresident
      updatedFilters.alphabetFilter = "";
      setAlphabetFilter("");
    }

    if (filterKey === searchType) {
      setSearchInput("");
    }

    setFilters(updatedFilters);
    setPage(1);
    updateURL(updatedFilters, 1);

    // Scroll to member list after a short delay
    setTimeout(() => {
      scrollToMemberList();
    }, 100);
  };

  const getActiveFilters = () => {
    const activeFiltersList = [];

    Object.entries(filters).forEach(([key, value]) => {
      if (
        value &&
        key !== "designation" &&
        key !== "currentOfficeBearers" &&
        key !== "memberRole" &&
        key !== "alphabetFilter" &&
        key !== "pastPresident" // Add pastPresident
      ) {
        const searchTypeObj = searchTypes.find((type) => type.value === key);
        activeFiltersList.push({
          key,
          label: searchTypeObj ? searchTypeObj.label.replace("By ", "") : key,
          value: value,
          type: "search",
        });
      }
    });

    if (filters.alphabetFilter) {
      activeFiltersList.push({
        key: "alphabetFilter",
        label: "First Name Starts With",
        value: filters.alphabetFilter,
        type: "alphabet",
      });
    }

    if (filters.currentOfficeBearers) {
      activeFiltersList.push({
        key: "currentOfficeBearers",
        label: "Role",
        value: "Current Office Bearers",
        type: "role",
      });
    } else if (filters.memberRole) {
      activeFiltersList.push({
        key: "memberRole",
        label: "Role",
        value: filters.memberRole,
        type: "role",
      });
    } else if (filters.pastPresident === "true") {
      // Check for boolean value
      activeFiltersList.push({
        key: "pastPresident",
        label: "Role",
        value: "Past President",
        type: "role",
      });
    } else if (filters.designation) {
      const roleData = designations.find(
        (role) => role._id === filters.designation
      );
      if (roleData) {
        activeFiltersList.push({
          key: "designation",
          label: "Role",
          value: roleData.name,
          type: "role",
        });
      }
    }

    return activeFiltersList;
  };

  const activeFiltersList = getActiveFilters();
  const hasActiveFiltersDisplay = activeFiltersList.length > 0;

  const startIndex = (page - 1) * entriesOption.value + 1;
  const endIndex = Math.min(page * entriesOption.value, totalDocs);

  const userId = getCookie("userId");

  const role = getCookie("role");

  const { data: permissionsData } = useUserModulePermissions(
    "Members",
    userId,
    {
      enabled: role === "maintainer" && userId && userId !== "undefined",
    }
  );

  return (
    <div className="flex h-screen p-4 gap-4 bg-white overflow-hidden">
      <div className="h-full">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 p-4 overflow-y-auto">
              <Header
                title="Member Management"
                desc="Comprehensive overview of all organization members with advanced filtering."
              />

              <div className="bg-white mt-4 rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
                <div className="p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    Search & Filter Members
                  </h2>

                  {/* Search Type Radio Buttons */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-4">
                      Search Type
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      {searchTypes.map((type) => (
                        <label
                          key={type.value}
                          className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                            searchType === type.value
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name="searchType"
                            value={type.value}
                            checked={searchType === type.value}
                            onChange={(e) =>
                              handleSearchTypeChange(e.target.value)
                            }
                            className="!sr-only"
                          />
                          <div
                            className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                              searchType === type.value
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {searchType === type.value && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                          <span className="text-sm font-medium">
                            {type.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Search Input and Buttons */}
                  <div className="flex flex-col lg:flex-row gap-6 lg:items-end">
                    <div className="w-full lg:flex-grow">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                        Search Value
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={getCurrentPlaceholder()}
                          className="w-full h-12 sm:h-14 px-4 sm:px-6 pr-10 sm:pr-12 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all duration-300 bg-gray-50 focus:bg-white shadow-sm"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleSearch()
                          }
                        />
                        <div className="absolute inset-y-0 right-3 sm:right-4 flex items-center">
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                      <button
                        onClick={handleSearch}
                        className="h-12 sm:h-14 w-full sm:w-auto px-6 sm:px-8 font-semibold rounded-xl transition-all duration-300 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
                      >
                        Search
                      </button>
                      <button
                        onClick={handleResetFilters}
                        className="h-12 sm:h-14 w-full sm:w-auto px-6 sm:px-8 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-300"
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Filters Display */}
              {hasActiveFiltersDisplay && (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center">
                        <svg
                          className="w-5 h-5 mr-2 text-blue-600 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
                          />
                        </svg>
                        Active Filters ({activeFiltersList.length})
                      </h3>
                      <button
                        onClick={handleResetFilters}
                        className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center transition-colors duration-200"
                      >
                        <svg
                          className="w-4 h-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Clear All
                      </button>
                    </div>
                  </div>

                  <div className="p-4 sm:p-6">
                    <div className="space-y-4 sm:space-y-0 sm:flex sm:flex-wrap sm:gap-4">
                      {activeFiltersList.map((filter, index) => (
                        <div
                          key={`${filter.key}-${index}`}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full sm:w-auto p-3 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200 hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex items-center space-x-4">
                            <div
                              className={`p-2 rounded-lg ${
                                filter.type === "role"
                                  ? "bg-green-100 text-green-700"
                                  : filter.type === "alphabet"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {filter.type === "role" ? (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                              ) : filter.type === "alphabet" ? (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 20l4-16m6 16l4-16M6 9h14M4 15h14"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                  />
                                </svg>
                              )}
                            </div>
                            <div>
                              <div className="flex flex-wrap items-center gap-1">
                                <span className="text-sm font-medium text-gray-600">
                                  {filter.label}:
                                </span>
                                <span
                                  className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                                    filter.type === "role"
                                      ? "bg-green-100 text-green-800"
                                      : filter.type === "alphabet"
                                      ? "bg-purple-100 text-purple-800"
                                      : "bg-blue-100 text-blue-800"
                                  }`}
                                >
                                  {filter.value}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {filter.type === "role"
                                  ? "Role Filter"
                                  : filter.type === "alphabet"
                                  ? "Alphabet Filter"
                                  : "Search Filter"}
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={() => removeFilter(filter.key)}
                            className="mt-3 sm:mt-0 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 group self-start sm:self-auto"
                            title={`Remove ${filter.label} filter`}
                          >
                            <svg
                              className="w-4 h-4 group-hover:scale-110 transition-transform duration-200"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Role Filter Buttons */}
              <div className="flex flex-wrap gap-4 mb-6">
                <button
                  onClick={() => handleRoleFilter("Current Office Bearers")}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    activeRole === "Current Office Bearers"
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                      : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Current Office Bearers
                </button>

                <button
                  onClick={() => handleRoleFilter("Founder Member")}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    activeRole === "Founder Member"
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                      : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Founder Member
                </button>

                {/* Past President Button - Now uses boolean */}
                <button
                  onClick={() => handleRoleFilter("Past President")}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    activeRole === "Past President"
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                      : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Past President
                </button>

                {designations.map((role) => (
                  <button
                    key={role._id}
                    onClick={() => handleRoleFilter(role.name)}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                      activeRole === role.name
                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                        : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {role.name}
                  </button>
                ))}
              </div>

              {/* Alphabet Filter */}
              {shouldShowAlphabetFilter() && (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-6">
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 px-6 py-4 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 20l4-16m6 16l4-16M6 9h14M4 15h14"
                        />
                      </svg>
                      Filter by First Name (A-Z)
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Available for {activeRole} members only
                    </p>
                  </div>

                  <div className="p-6">
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: 26 }, (_, i) => {
                        const letter = String.fromCharCode(65 + i);
                        return (
                          <button
                            key={letter}
                            onClick={() => handleAlphabetFilter(letter)}
                            className={`h-10 w-10 rounded-lg font-semibold text-sm transition-all duration-300 ${
                              alphabetFilter === letter
                                ? "bg-blue-700 text-white shadow-lg transform scale-110"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900"
                            }`}
                          >
                            {letter}
                          </button>
                        );
                      })}
                    </div>

                    {alphabetFilter && (
                      <div className="mt-4 flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                        <span className="text-sm text-green-800 font-medium">
                          Showing members whose first name starts with "
                          {alphabetFilter}"
                        </span>
                        <button
                          onClick={() => handleAlphabetFilter("")}
                          className="text-green-600 hover:text-green-700 font-medium text-sm"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center mb-4">
                <DropdownSelect
                  label="Entries"
                  selected={entriesOption}
                  onChange={handleEntriesChange}
                  options={entriesOptions}
                />

                {!isLoading && !isError && totalDocs > 0 && (
                  <div className="text-sm text-gray-600">
                    Showing {startIndex} to {endIndex} of {totalDocs} entries
                  </div>
                )}
              </div>

              <div className="mb-4 -translate-y-3">
                <ExportButtons model="members" />
              </div>

              {/* Member List Section with Ref */}
              <div ref={memberListRef}>
                {isLoading || isFetching ? (
                  <div className="flex justify-center items-center py-8">
                    <p>Loading members...</p>
                  </div>
                ) : isError ? (
                  <div className="flex justify-center items-center py-8">
                    <p className="text-red-500">Failed to fetch members.</p>
                  </div>
                ) : memberList.length === 0 ? (
                  <div className="flex justify-center items-center py-8">
                    <p className="text-gray-500">No members found.</p>
                  </div>
                ) : (
                  <>
                    <MemberListTable
                      members={memberList}
                      refetch={refetch}
                      startIndex={(page - 1) * entriesOption.value}
                      isHide={true}
                      permissionsData={permissionsData?.data ?? null}
                    />

                    {totalPages > 1 && (
                      <div className="mt-6">
                        <Pagination
                          totalPages={totalPages}
                          currentPage={page}
                          onPageChange={handlePageChange}
                          showPages={5}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default MembersList;
